import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Box, Theme } from "@outschool/backpack";
import { CategoryPage, SearchFilters } from "@outschool/gql-backend-generated";
import { ContentfulCategoryPageQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { onboardingQuizPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { TrustpilotMicroCombo } from "@outschool/ui-components-website";
import { Container } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";

import { HeroFilters } from "../filters/HeroFilters";
import {
  DesktopCategoryHero,
  DesktopHeroImage,
  DesktopHeroTitle,
  HeroValueProps,
  MobileCategoryHero,
  MobileHeroTitle,
} from "../HeroComponents";

type ContentfulCategoryPage = NonNullable<
  NonNullable<
    NonNullable<
      ContentfulCategoryPageQuery["contentfulCategoryPageCollection"]
    >["items"][number]
  >
>;

export default function CuratedCategoryHeroSection({
  category,
  page,
  searchFilters,
  updateSearchFilters,
  Breadcrumbs,
  isTutoringCLP = false,
}: {
  category: CategoryPage;
  page: ContentfulCategoryPage;
  searchFilters: SearchFilters;
  updateSearchFilters: (payload: SearchFilters) => void;
  Breadcrumbs: React.ReactElement | null;
  isTutoringCLP?: boolean;
}) {
  const { isLoggedIn } = useSession();
  const isMobile = useIsMobile();
  const navigate = useNavigation();
  const { t } = useTranslation(
    "client\\components\\categories\\CuratedCategoryHeroSection",
  );

  const contentfulValueProps = documentToReactComponents(
    page.heroValueProps?.json || "",
  );

  const heroDescription = documentToReactComponents(
    page.heroDescription?.json || "",
  );
  if (isMobile) {
    return (
      <MobileCategoryHero
        Breadcrumbs={Breadcrumbs}
        HeroContent={
          <Container sx={{ py: "medium" }}>
            <MobileHeroTitle title={page.heroTitle ?? ""} />
            <HeroValueProps contentfulValueProps={contentfulValueProps} />
            {!isTutoringCLP && <TrustpilotMicroCombo sx={{ mt: 20 }} />}
            {!isLoggedIn && (
              <TrackedButton
                trackingName="logged-out-clp-hero-button-onboarding-quiz"
                variant="contained"
                sx={(theme: Theme) => ({
                  [theme.breakpoints.up("md")]: {
                    alignSelf: "start",
                  },
                })}
                onClick={() =>
                  navigate(onboardingQuizPath(), { hardNav: true })
                }
              >
                {t("Get Started")}
              </TrackedButton>
            )}
          </Container>
        }
        isTutoringCLP={isTutoringCLP}
      />
    );
  }

  return (
    <DesktopCategoryHero
      Breadcrumbs={Breadcrumbs}
      HeroTitle={<DesktopHeroTitle title={page.heroTitle ?? ""} />}
      HeroImage={
        !isTutoringCLP && (
          <DesktopHeroImage imageUrl={page.heroImage?.url ?? ""} />
        )
      }
      HeroDescription={heroDescription}
      isTutoringCLP={isTutoringCLP}
      HeroContent={
        <Box>
          <Box>
            <HeroValueProps contentfulValueProps={contentfulValueProps} />
            {!isTutoringCLP && (
              <Box
                sx={(theme: Theme) => ({
                  paddingTop: theme.spacing(16),
                })}
              >
                <TrustpilotMicroCombo />
              </Box>
            )}
          </Box>
          {!isLoggedIn && (
            <Box
              sx={(theme: Theme) => ({
                paddingTop: theme.spacing(10),
              })}
            >
              <TrackedButton
                trackingName="logged-out-clp-hero-button-onboarding-quiz"
                variant="contained"
                sx={(theme: Theme) => ({
                  [theme.breakpoints.up("md")]: {
                    alignSelf: "start",
                  },
                })}
                onClick={() =>
                  navigate(onboardingQuizPath(), { hardNav: true })
                }
              >
                {t("Get Started")}
              </TrackedButton>
            </Box>
          )}
        </Box>
      }
      HeroFilters={
        <HeroFilters
          categorySlug={category.slug}
          updateSearchFilters={updateSearchFilters}
          searchFilters={searchFilters}
        />
      }
    />
  );
}
